import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabase';
import { UserPlus, RefreshCw, CheckCircle, Clock, XCircle, AlertCircle, Eye, Filter, ChevronDown, ChevronUp, ExternalLink, Trash2 } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface SignupStep {
  id: string;
  step_name: string;
  step_order: number;
  step_status: string;
  error_message: string | null;
  metadata: any;
  started_at: string;
  completed_at: string | null;
}

interface SignupRequest {
  id: string;
  order_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company: string | null;
  product_name: string | null;
  total_eur: number | null;
  credits_amount: number;
  user_id: string | null;
  status: string;
  error_message: string | null;
  webhook_data?: any;
  created_at: string;
  completed_at: string | null;
  user_email: string | null;
  user_first_name: string | null;
  steps: SignupStep[] | null;
}

const STEP_LABELS: Record<string, string> = {
  webhook_received: 'Webhook Ontvangen',
  user_created: 'Account Aangemaakt',
  credits_assigned: 'Credits Toegekend',
  welcome_email_sent: 'Welkom Email Verzonden',
};

export function AdminSignups() {
  const [loading, setLoading] = useState(true);
  const [signups, setSignups] = useState<SignupRequest[]>([]);
  const [selectedSignup, setSelectedSignup] = useState<SignupRequest | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [deleteSignup, setDeleteSignup] = useState<SignupRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [processingSignup, setProcessingSignup] = useState<string | null>(null);

  const loadSignups = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_signup_overview')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      let filteredData = data || [];
      if (statusFilter !== 'all') {
        filteredData = filteredData.filter(s => s.status === statusFilter);
      }

      setSignups(filteredData);
    } catch (error) {
      console.error('Error loading signups:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSignups();
  }, [statusFilter]);

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getSummary = () => {
    return {
      total: signups.length,
      received: signups.filter(s => s.status === 'received').length,
      processing: signups.filter(s => s.status === 'processing').length,
      completed: signups.filter(s => s.status === 'completed').length,
      failed: signups.filter(s => s.status === 'failed').length,
    };
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: any }> = {
      received: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      processing: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: RefreshCw },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    };

    const style = styles[status] || styles.received;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-gray-400" />;
      case 'skipped':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatDuration = (startedAt: string, completedAt: string | null) => {
    if (!completedAt) return '-';
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const durationMs = end - start;
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${(durationMs / 1000).toFixed(2)}s`;
  };

  const summary = getSummary();

  const handleDeleteSignup = async () => {
    if (!deleteSignup) return;

    setIsDeleting(true);
    try {
      const { error: stepsError } = await supabase
        .from('signup_steps')
        .delete()
        .eq('signup_request_id', deleteSignup.id);

      if (stepsError) throw stepsError;

      const { error } = await supabase
        .from('signup_requests')
        .delete()
        .eq('id', deleteSignup.id);

      if (error) throw error;

      setSignups(prev => prev.filter(s => s.id !== deleteSignup.id));
      setDeleteSignup(null);
    } catch (error) {
      console.error('Error deleting signup:', error);
      alert('Fout bij verwijderen van signup');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleProcessSignup = async (signupId: string) => {
    if (processingSignup) return;

    setProcessingSignup(signupId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Niet geauthenticeerd');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-signup-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            signup_request_id: signupId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process signup');
      }

      // Reload signups to show updated status
      await loadSignups();
      alert('Signup succesvol verwerkt!');
    } catch (error: any) {
      console.error('Error processing signup:', error);
      alert(`Fout bij verwerken signup: ${error.message}`);
    } finally {
      setProcessingSignup(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <UserPlus className="w-6 h-6" />
                Signup Monitoring
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Nieuwe gebruikers registraties en onboarding status
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  {statusFilter === 'all' ? 'Alle' : statusFilter}
                </button>
                {showFilterDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="py-1">
                      {['all', 'received', 'processing', 'completed', 'failed'].map((status) => (
                        <button
                          key={status}
                          onClick={() => {
                            setStatusFilter(status);
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            statusFilter === status ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {status === 'all' ? 'Alle statussen' : status}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={loadSignups}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Ververs
              </button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Totaal</div>
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
            </div>
            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Ontvangen</div>
              <div className="text-2xl font-bold text-blue-600">{summary.received}</div>
            </div>
            <div className="bg-white border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Bezig</div>
              <div className="text-2xl font-bold text-yellow-600">{summary.processing}</div>
            </div>
            <div className="bg-white border border-green-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Voltooid</div>
              <div className="text-2xl font-bold text-green-600">{summary.completed}</div>
            </div>
            <div className="bg-white border border-red-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Mislukt</div>
              <div className="text-2xl font-bold text-red-600">{summary.failed}</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {signups.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Geen signups ontvangen</p>
              <p className="text-sm text-gray-500 mt-1">
                Nieuwe gebruiker registraties via Zapier verschijnen hier automatisch
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Ontvangen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Order ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Nieuwe Gebruiker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {signups.map((signup) => (
                    <React.Fragment key={signup.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleRowExpansion(signup.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {expandedRows.has(signup.id) ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(signup.created_at).toLocaleString('nl-NL')}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                            {signup.order_id}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div>
                            <div className="font-medium text-gray-900">
                              {signup.first_name} {signup.last_name}
                            </div>
                            <div className="text-gray-600 text-xs">{signup.email}</div>
                            {signup.company && (
                              <div className="text-gray-500 text-xs">{signup.company}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {getStatusBadge(signup.status)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            {(signup.status === 'received' || signup.status === 'failed') && (
                              <button
                                onClick={() => handleProcessSignup(signup.id)}
                                disabled={processingSignup === signup.id}
                                className="text-green-600 hover:text-green-700 font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={signup.status === 'received' ? 'Verwerk Signup' : 'Retry'}
                              >
                                {processingSignup === signup.id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                                {signup.status === 'received' ? 'Verwerk' : 'Retry'}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedSignup(signup);
                                setShowDetailModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                              title="Bekijk details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {signup.user_id && (
                              <a
                                href={`/admin/users/${signup.user_id}`}
                                className="text-gray-600 hover:text-gray-800"
                                title="Bekijk gebruiker"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              onClick={() => setDeleteSignup(signup)}
                              className="text-red-600 hover:text-red-700"
                              title="Verwijder signup"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRows.has(signup.id) && signup.steps && signup.steps.length > 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-4 bg-gray-50">
                            <div className="ml-8">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Signup Stappen</h4>
                              <div className="space-y-2">
                                {signup.steps.map((step, index) => (
                                  <div
                                    key={step.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg ${
                                      step.step_status === 'success' ? 'bg-green-50' :
                                      step.step_status === 'failed' ? 'bg-red-50' :
                                      'bg-white border border-gray-200'
                                    }`}
                                  >
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                                      {index + 1}
                                    </div>
                                    {getStepStatusIcon(step.step_status)}
                                    <div className="flex-1">
                                      <div className="font-medium text-sm text-gray-900">
                                        {STEP_LABELS[step.step_name] || step.step_name}
                                      </div>
                                      {step.error_message && (
                                        <div className="text-xs text-red-600 mt-1">
                                          {step.error_message}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {formatDuration(step.started_at, step.completed_at)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showDetailModal && selectedSignup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Signup Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                      <div className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded">
                        {selectedSignup.order_id}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <div>{getStatusBadge(selectedSignup.status)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Naam</label>
                      <div className="text-sm text-gray-900">
                        {selectedSignup.first_name} {selectedSignup.last_name}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <div className="text-sm text-gray-900">{selectedSignup.email}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefoon</label>
                      <div className="text-sm text-gray-900">{selectedSignup.phone || '-'}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bedrijf</label>
                      <div className="text-sm text-gray-900">{selectedSignup.company || '-'}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                      <div className="text-sm text-gray-900">{selectedSignup.product_name || '-'}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bedrag</label>
                      <div className="text-sm text-gray-900">
                        {selectedSignup.total_eur ? `€${selectedSignup.total_eur.toFixed(2)}` : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Credits</label>
                      <div className="text-sm font-medium text-blue-600">
                        {selectedSignup.credits_amount.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {selectedSignup.error_message && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-medium text-red-900 mb-1">Error</div>
                          <div className="text-sm text-red-800">{selectedSignup.error_message}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSignup.user_id && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-green-900 mb-1">Account Aangemaakt</div>
                          <div className="text-sm text-green-800">
                            {selectedSignup.user_first_name} ({selectedSignup.user_email})
                          </div>
                        </div>
                        <a
                          href={`/admin/users/${selectedSignup.user_id}`}
                          className="inline-flex items-center gap-2 text-green-700 hover:text-green-800 font-medium"
                        >
                          Bekijk Gebruiker
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  )}

                  {selectedSignup.steps && selectedSignup.steps.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Stappen Timeline</h4>
                      <div className="space-y-3">
                        {selectedSignup.steps.map((step, index) => (
                          <div
                            key={step.id}
                            className={`flex items-start gap-3 p-4 rounded-lg border ${
                              step.step_status === 'success' ? 'bg-green-50 border-green-200' :
                              step.step_status === 'failed' ? 'bg-red-50 border-red-200' :
                              'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center text-sm font-medium text-gray-600">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {getStepStatusIcon(step.step_status)}
                                <span className="font-medium text-gray-900">
                                  {STEP_LABELS[step.step_name] || step.step_name}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  step.step_status === 'success' ? 'bg-green-100 text-green-700' :
                                  step.step_status === 'failed' ? 'bg-red-100 text-red-700' :
                                  step.step_status === 'pending' ? 'bg-gray-100 text-gray-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {step.step_status}
                                </span>
                              </div>
                              {step.error_message && (
                                <div className="mt-2 text-sm text-red-600">
                                  {step.error_message}
                                </div>
                              )}
                              <div className="mt-2 text-xs text-gray-500 flex items-center gap-4">
                                <span>Start: {new Date(step.started_at).toLocaleString('nl-NL')}</span>
                                {step.completed_at && (
                                  <span>Duur: {formatDuration(step.started_at, step.completed_at)}</span>
                                )}
                              </div>
                              {step.metadata && Object.keys(step.metadata).length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                    Metadata
                                  </summary>
                                  <pre className="mt-1 text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                                    {JSON.stringify(step.metadata, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedSignup.webhook_data && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Volledige Webhook Data</h4>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words">
                          {JSON.stringify(selectedSignup.webhook_data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteSignup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Signup Verwijderen</h3>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Weet je zeker dat je deze signup wilt verwijderen?
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">
                      {deleteSignup.first_name} {deleteSignup.last_name}
                    </div>
                    <div className="text-gray-600">{deleteSignup.email}</div>
                    <div className="text-gray-500 text-xs mt-1">
                      Order: {deleteSignup.order_id}
                    </div>
                  </div>
                </div>
                {deleteSignup.user_id && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      Let op: Dit verwijdert alleen de signup registratie, niet het aangemaakte gebruikersaccount.
                    </p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteSignup(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDeleteSignup}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Verwijderen...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Verwijderen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
